/**
 * NARRATIVA DA PÁGINA PRINCIPAL — capítulos 001 a 005.
 *
 * POSICIONAMENTO (dono, 2026-09-19): a HTS NÃO se vende por funcionalidade que o mercado já oferece
 * bem (gráfico, chart trading, curva de DI, calendário, limite de perda). Comparar feature por
 * feature a coloca como "mais uma plataforma". O que se vende é a INTEGRAÇÃO: execução +
 * inteligência de mercado + evolução operacional no mesmo fluxo de decisão.
 *   - Antes da execução: contexto de formação de preço (juros, FX, política monetária, risco, notícia).
 *   - Durante: leitura ao vivo (curva, dólar, notícia, HT TV) na tela em que se executa.
 *   - Depois: a operação vira dado (Execução → Diário → Performance → Métricas → Evolução).
 *
 * ⚠️ SÓ O QUE O PLANO RETAIL ENTREGA. O Founding Access recebe o módulo `terminal`, nunca `mesa`
 * (ver MarkDown/PLATAFORMA_CENTRAL_A2_MODULES.sql e `requiresModule` em nav-config.ts). Mandato,
 * flags, fases, trilha/LMS e carreira são da mesa proprietária — o texto interno do comercial cita
 * esses itens, mas nesta página seriam promessa falsa. Pelo mesmo motivo NÃO há bloco de gestão de
 * risco: limites e a categoria "Risco" do terminal são só da mesa (dono, 2026-09-19). HT TV e
 * diário automático (inclusive com trade do simulador) o retail tem — confirmado pelo dono.
 *
 * "Informação antes" é contexto, expectativa e precificação — NUNCA informação privilegiada. A
 * página diz isso no hero, na caixa do capítulo 003 e no FAQ. Não tirar.
 */
import { Chapter } from "@/components/site/shared";

// ============================================================================
// MARQUEE
// ============================================================================
const TICKERS = [
  { sym: "WIN", px: "139.450", delta: "+1.24%", up: true },
  { sym: "WDO", px: "5.087,5", delta: "-0.32%", up: false },
  { sym: "DI1F27", px: "13.42%", delta: "+8 bps", up: true },
  { sym: "DXY", px: "101,84", delta: "+0.21%", up: true },
  { sym: "PETR4", px: "39,15", delta: "-1.20%", up: false },
  { sym: "VALE3", px: "62,08", delta: "+0.55%", up: true },
  { sym: "USDBRL", px: "5,089", delta: "-0.31%", up: false },
  { sym: "IBOV", px: "129.842", delta: "+0.92%", up: true },
  { sym: "UST10Y", px: "4.21%", delta: "+3 bps", up: true },
  { sym: "S&P500", px: "5.847", delta: "+0.44%", up: true },
];

export function TickerMarquee() {
  const items = [...TICKERS, ...TICKERS];
  return (
    <div className="lv2-marquee" aria-hidden>
      <div className="lv2-marquee-track">
        {items.map((t, i) => (
          <div key={i} className="lv2-ticker">
            <span className="sym">{t.sym}</span>
            <span className="px">{t.px}</span>
            <span className={`delta ${t.up ? "up" : "down"}`}>{t.delta}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// CARD DE FUNCIONALIDADE
// ============================================================================
function FeatCard({ icon, label, title, desc, tags, accent }: { icon: string; label: string; title: string; desc: string; tags?: readonly string[]; accent?: boolean }) {
  return (
    <div className={`hts-feat-card ${accent ? "is-accent" : ""}`} data-cursor-hover>
      <div className="hts-feat-icon">{icon}</div>
      <div className="hts-feat-label">{label}</div>
      <div className="hts-feat-title">{title}</div>
      <div className="hts-feat-desc">{desc}</div>
      {tags && (
        <div className="hts-tags">
          {tags.map((t) => <span key={t} className="hts-tag">{t}</span>)}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CONTEÚDO
// ============================================================================
const CAMADAS = [
  ["▶", "EXECUÇÃO", "Onde você opera", "Gráfico, book, DOM, chart trading e ordens. A base de qualquer plataforma profissional — e está toda aqui."],
  ["⌁", "INTELIGÊNCIA DE MERCADO", "O que está formando o preço", "Juros, política monetária, dólar global, risco e notícia contextualizada, na mesma tela em que você executa."],
  ["↗", "EVOLUÇÃO OPERACIONAL", "O que a operação ensina", "Cada execução alimenta o diário, o histórico e as métricas. Você mede o processo, não só o resultado."],
] as const;

const TRADICIONAL = [
  ["Começa pelo ativo.", "Vê o WDO subir e tenta descobrir o motivo depois que ele andou."],
  ["Cada dado numa janela solta.", "Curva, calendário e notícia até existem, mas a ligação entre eles você monta de cabeça."],
  ["Notícia como feed bruto.", "Sem dizer qual ativo a manchete mexe."],
  ["Chega ao pregão sem roteiro.", "Agenda e cenário descobertos no meio do pregão."],
  ["O mês termina no P&L.", "Você sabe quanto ganhou ou perdeu, mas não como chegou lá."],
] as const;

const AMBIENTE = [
  ["Como a curva de juros está se movendo.", "Estrutura, inclinação e volatilidade do DI."],
  ["Como o dólar está globalmente.", "Força do dólar, FX e o que o mundo está fazendo."],
  ["Qual é o regime de risco agora.", "Apetite a risco global e doméstico."],
  ["Se a política monetária foi reprecificada.", "Probabilidades de Copom e FOMC em tempo real."],
  ["O que está no radar e no noticiário.", "Agenda macro e notícias ligadas aos ativos que elas afetam."],
] as const;

const FASES = [
  ["ANTES DA EXECUÇÃO", "Contexto", "Você chega à decisão sabendo o que juros, dólar, expectativa monetária e agenda estão dizendo — e o que já está no preço."],
  ["DURANTE A EXECUÇÃO", "Leitura ao vivo", "Curva, dólar, notícia e a HT TV acompanham o pregão na mesma tela em que você executa."],
  ["DEPOIS DA EXECUÇÃO", "A operação vira dado", "A execução alimenta sozinha o diário de trade, o histórico e as métricas de performance."],
] as const;

const CADEIA = ["Execução", "Diário", "Performance", "Métricas", "Evolução"];

/** Por bloco, como o comercial apresenta (dono, 2026-09-19). Juros vem primeiro e em destaque: para
 *  quem opera WDO, DI e índice vale mais que qualquer indicador gráfico. Trading/execução vem por
 *  último de propósito — é a base, não o diferencial. */
const BLOCOS = [
  ["%", "JUROS E MERCADO BRASILEIRO", "A dinâmica de juros dentro da leitura do ativo", "Pra quem opera WDO, DI e índice, isso vale mais do que mais um RSI ou MACD.", ["Curva DI", "Estrutura DI", "Superfície de juros", "Volatilidade DI", "Precificação Copom", "Estrutura temporal"], true],
  ["◉", "MACRO E INTERNACIONAL", "O mundo dentro do fluxo operacional", "Não é ter um calendário. É ter o macro na tela em que você decide.", ["FOMC pricing", "CPI EUA", "Calendário econômico", "FX", "Força do dólar", "Sentimento global", "Risco global", "Polymarket"], false],
  ["N", "NOTÍCIAS COM CONTEXTO", "Menos informação bruta pra interpretar sozinho", "O feed já chega classificado e ligado aos ativos que a notícia mexe.", ["Feed em tempo real", "Sentimento", "Trending por ativo", "Ativos relacionados"], false],
  ["▷", "HT TV", "Contexto editorial dentro do terminal", "Cenário de abertura, agenda do dia, juros e internacional durante o pregão. Contexto para a decisão, não call.", ["Cenário de abertura", "Agenda do dia", "Leitura de juros", "Internacional", "Acompanhamento do pregão"], false],
  ["↗", "PERFORMANCE", "O trader de hoje alimenta o de amanhã", "Frequência, consistência e comportamento, não só o P&L do mês. Funciona também com as operações do simulador.", ["Diário de trade automático", "Histórico de operações", "Métricas operacionais", "Relatório de performance"], false],
  ["▦", "WORKSPACE", "O terminal do seu jeito", "Monte a tela como você trabalha e leve o ambiente pra qualquer máquina.", ["Widgets drag & drop", "Layouts salvos", "Multi-monitor", "Janelas no HTS Desktop", "Windows, Mac e navegador", "Celular via webapp"], false],
  ["▶", "TRADING E EXECUÇÃO", "A base, completa", "Tudo o que se espera de uma plataforma profissional. No lançamento, a execução roda em simulador com dado real.", ["Gráficos avançados", "Chart Trading", "SuperDOM", "Book de ofertas", "OMS", "Ordens e posições", "Multi-chart", "Crosshair sincronizado", "Indicadores e desenho"], false],
] as const;

/** Depoimentos de operadores reais, com uso de nome autorizado (dono, 2026-09-14). */
const DEPOIMENTOS = [
  ["A maior mudança foi parar de olhar apenas o gráfico. Quando você começa a enxergar contexto, juros, risco e o que o mercado já está precificando, a leitura muda completamente.", "LUCAS VIEIRA", true],
  ["Antes eu precisava abrir várias telas para tentar montar o cenário. Na HTS, mercado, notícia, calendário e leitura quantitativa passam a fazer parte da mesma decisão.", "PEDRO CHEIN", false],
  ["Operador profissional não pode descobrir o motivo do movimento depois que ele aconteceu. Você precisa chegar ao pregão sabendo o que observar e quais variáveis podem mudar o jogo.", "MATHEUS ROPELATO", false],
  ["Ter preço é básico. O que faz diferença é conseguir interpretar expectativa, regime, risco e precificação antes de tomar a decisão. É isso que transforma dado em inteligência.", "CAIO PASSOS", true],
] as const;

const LEAD = { maxWidth: 760, fontSize: 17, color: "#b8b8b8", lineHeight: 1.7 } as const;
const STRONG = { color: "#fff", fontWeight: 500 } as const;

// ============================================================================
// CAPÍTULOS 001–005
// ============================================================================
/**
 * Mídia do produto nos capítulos (23/09/2026). Só a página do PARCEIRO liga — decisão do dono:
 * "faz separado, porque assim que sair a pré-venda vamos mexer na página principal também".
 * O texto continua num arquivo só; o que muda entre as duas páginas é a mídia.
 *
 * `risco.mp4` existe no repositório e fica DE FORA de propósito: limites, mandato e o monitor de
 * risco são módulo da MESA, e quem compra o plano retail não recebe — mostrar aqui venderia o que
 * o comprador não vai ter (mesma regra do cabeçalho deste arquivo).
 */
const VIDEOS = {
  ambiente: [
    { src: "/videos/cpi.mp4", legenda: "CPI e o que ele faz com a curva" },
    { src: "/videos/sentimento.mp4", legenda: "Sentimento e força do dólar" },
    { src: "/videos/regime.mp4", legenda: "Regime de risco do dia" },
  ],
  processo: [{ src: "/videos/noticias.mp4", legenda: "Notícia classificada por ativo" }],
} as const;

function VideosDoCapitulo({ itens }: { itens: readonly { src: string; legenda: string }[] }) {
  return (
    <div className="hts-video-grid">
      {itens.map((v) => (
        <figure key={v.src} className="hts-video-card">
          {/* `muted` + `playsInline` porque autoplay com som é bloqueado no navegador e no iOS. */}
          <video src={v.src} autoPlay loop muted playsInline preload="metadata" />
          <figcaption>{v.legenda}</figcaption>
        </figure>
      ))}
    </div>
  );
}

/**
 * `semProva`: a página do parceiro não mostra o capítulo 005 (decisão do dono, 23/09). Lá a prova
 * social que importa é a do próprio parceiro, que indicou o cliente — "535 operadores da mesa"
 * fala de outra coisa e alonga a página antes do checkout.
 */
export function NarrativeChapters({ comMidia = false, semProva = false }: { comMidia?: boolean; semProva?: boolean } = {}) {
  return (
    <>
      <Chapter
        id="ch-produto"
        number="001"
        eyebrow="O que é a HTS"
        title={<>Não é mais uma plataforma.<br /><span className="lv2-outlined">É o seu processo de decisão inteiro, num lugar só.</span></>}
      >
        <div style={{ gridColumn: "1 / -1" }}>
          <p style={LEAD}>
            A HTS é um terminal profissional feito para o trader de B3. Ela conecta{" "}
            <strong style={STRONG}>execução, juros, macro, notícia e performance</strong> no mesmo
            fluxo operacional. O objetivo não é mostrar para onde o preço vai — é ajudar você a
            entender o ambiente que está formando esse preço, executar essa leitura e depois medir
            a sua evolução.
          </p>
          <div className="hts-feat-grid">
            {CAMADAS.map(([icon, label, title, desc]) => (
              <FeatCard key={label} icon={icon} label={label} title={title} desc={desc} />
            ))}
          </div>
        </div>
      </Chapter>

      <TickerMarquee />

      <Chapter
        id="ch-ambiente"
        number="002"
        eyebrow="Do ativo ao ambiente"
        title={<>“O dólar está subindo.”<br /><span className="lv2-outlined">Mas o que está fazendo ele subir?</span></>}
      >
        <div style={{ gridColumn: "1 / -1" }}>
          <p style={LEAD}>
            Uma plataforma tradicional mostra que o WDO está subindo. A HTS também mostra. A
            diferença é que, na mesma tela, você vê o que juros, dólar global, expectativa monetária
            e risco estão fazendo naquele momento. É assim que uma mesa profissional olha o mercado:
            não o ativo isolado, mas as variáveis que participam da formação do preço.{" "}
            <strong style={STRONG}>Vale principalmente para quem opera dólar, índice, DI e ativos sensíveis a juros.</strong>
          </p>
          <div className="hts-compare">
            <div className="hts-compare-col">
              <div className="hts-compare-label">Plataforma tradicional</div>
              <div className="hts-compare-head">Mostra o que<br /><span>o preço fez.</span></div>
              <ul className="hts-compare-list">
                {TRADICIONAL.map(([titulo, desc]) => (
                  <li key={titulo}>
                    <span className="hts-compare-mark">×</span>
                    <span><b>{titulo}</b><br />{desc}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="hts-compare-col is-pro">
              <div className="hts-compare-label">HTS</div>
              <div className="hts-compare-head">Mostra o ambiente<br /><span>que move o preço.</span></div>
              <ul className="hts-compare-list">
                {AMBIENTE.map(([titulo, desc]) => (
                  <li key={titulo}>
                    <span className="hts-compare-mark">✓</span>
                    <span><b>{titulo}</b><br />{desc}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {comMidia && <VideosDoCapitulo itens={VIDEOS.ambiente} />}
        </div>
      </Chapter>

      <Chapter
        id="ch-processo"
        number="003"
        eyebrow="Antes, durante e depois"
        title={<>A HTS não termina<br /><span className="lv2-outlined">quando a posição fecha.</span></>}
      >
        <div style={{ gridColumn: "1 / -1" }}>
          <div className="lv2-phases">
            {FASES.map(([num, name, desc]) => (
              <div key={num} className="lv2-phase" data-cursor-hover>
                <div className="num">{num}</div>
                <div className="name">{name}</div>
                <div className="desc">{desc}</div>
              </div>
            ))}
          </div>

          {/* Notícia e diário LADO A LADO (pedido do dono, 23/09): são as duas pontas do mesmo
              capítulo — o que chega antes da decisão e o que sobra depois dela. Empilhados, a
              relação entre os dois se perdia no rolar da página.

              A imagem do diário vem com os VALORES OCULTOS de propósito — o dono cobriu os números
              antes de mandar. Resultado em página de venda é lido como promessa de ganho, e o
              argumento aqui é a estrutura, que não precisa de cifra. */}
          {comMidia && (
            <div className="hts-midia-dupla">
              <figure className="hts-video-card">
                <video src={VIDEOS.processo[0].src} autoPlay loop muted playsInline preload="metadata" />
                <figcaption>{VIDEOS.processo[0].legenda}</figcaption>
              </figure>
              <figure className="hts-video-card">
                <img src="/img/diario-de-trade.png" alt="Diário de trade da HTS: operações do dia, planejamento pré-mercado e review pós-mercado" loading="lazy" decoding="async" />
                <figcaption>
                  O que você planejou, o que executou e o que aprendeu — no mesmo lugar onde a ordem
                  foi mandada. Valores omitidos nesta imagem.
                </figcaption>
              </figure>
            </div>
          )}

          <div className="hts-chain" aria-label="Execução, diário, performance, métricas e evolução">
            {CADEIA.map((etapa, i) => (
              <span key={etapa} className="hts-chain-step">
                {etapa}
                {i < CADEIA.length - 1 && <span className="hts-chain-arrow" aria-hidden>→</span>}
              </span>
            ))}
          </div>
          <p style={{ ...LEAD, margin: "0 auto", textAlign: "center" }}>
            Em vez de terminar o mês sabendo só quanto ganhou ou perdeu, você enxerga{" "}
            <strong style={STRONG}>frequência, consistência e comportamento</strong> — o processo
            que produziu aquele resultado.
          </p>

          {/* Para-raio da página: "contexto antes da decisão" sem esta ressalva é lido como promessa
              de vantagem indevida. */}
          <div className="hts-infobox" style={{ marginTop: 56 }}>
            <h3>Contexto antes<br /><span className="lv2-outlined">não é informação privilegiada.</span></h3>
            <p>
              É saber o calendário, conhecer as expectativas e acompanhar curva de juros, risco,
              dólar e política monetária{" "}
              <strong style={STRONG}>antes de precisar decidir</strong>. Tudo público, tudo
              disponível — só que organizado no mesmo lugar em que você opera. A HTS não dá call nem
              diz onde comprar ou vender.
            </p>
          </div>
        </div>
      </Chapter>

      <Chapter
        id="ch-dentro"
        number="004"
        eyebrow="O que tem dentro"
        title={<>Cada bloco conectado aos outros.<br /><span className="lv2-outlined">Tudo num plano só.</span></>}
      >
        <div style={{ gridColumn: "1 / -1" }}>
          <p style={LEAD}>
            A lista prova a capacidade da plataforma, mas o produto é a ligação entre os blocos.{" "}
            <strong style={STRONG}>Sem plano superior, sem módulo pago à parte e sem taxa de market data por fora.</strong>
          </p>
          <div className="hts-feat-grid">
            {BLOCOS.map(([icon, label, title, desc, tags, accent]) => (
              <FeatCard key={label} icon={icon} label={label} title={title} desc={desc} tags={tags} accent={accent} />
            ))}
          </div>
        </div>
      </Chapter>

      {!semProva && (
        <Chapter
          id="ch-prova"
          number="005"
          eyebrow="Prova"
          title={<>Nasceu numa mesa profissional.<br /><span className="lv2-outlined">Roda todo dia desde 2021.</span></>}
        >
          <div style={{ gridColumn: "1 / -1" }}>
            <p style={LEAD}>
              A HTS não é um produto que a gente construiu pra vender. É a ferramenta que já roda
              todo dia, em estrutura institucional completa, com{" "}
              <strong style={STRONG}>535 operadores profissionais</strong>.
              Isso não é um lançamento. É uma abertura.
            </p>
            <div className="hts-quote-grid">
              {DEPOIMENTOS.map(([texto, nome, destaque]) => (
                <div key={nome} className={`hts-quote-card ${destaque ? "is-accent" : ""}`} data-cursor-hover>
                  <div className="hts-quote-mark">❞</div>
                  <p className="hts-quote-text">{texto}</p>
                  <div className="hts-quote-name">{nome}</div>
                </div>
              ))}
            </div>
          </div>
        </Chapter>
      )}
    </>
  );
}
